import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import { API_BASE_URL } from '../../config';

// CBA Statuses:
// 0: Not Visited (Silver/Grey)
// 1: Not Answered (Red)
// 2: Answered (Green)
// 3: Marked for Review (Purple)
// 4: Answered & Marked for Review (Purple with dot)

export default function CBATest() {
  const { instituteId, testId } = useParams();
  const navigate = useNavigate();
  
  const [testConf, setTestConf] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [sections, setSections] = useState([]); // { name: 'Physics', questionIndices: [0, 1, 2...] }
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const [answers, setAnswers] = useState({}); // { qId: optionText }
  const [statuses, setStatuses] = useState({}); // { qId: statusNumber }
  const [timeLeft, setTimeLeft] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [scoreData, setScoreData] = useState(null);

  const [showSummary, setShowSummary] = useState(false);
  const [windowLeaveCount, setWindowLeaveCount] = useState(0);
  const [totalOutsideTime, setTotalOutsideTime] = useState(0);

  // Auth/User
  const user = JSON.parse(localStorage.getItem('user') || "{}");

  const fetchTestData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/tests/${testId}`);
      const test = res.data;
      setTestConf(test);
      setTimeLeft(test.durationMins * 60);

      const qIds = test.questionIds;
      if (!qIds || qIds.length === 0) {
        toast.error("This test has no questions.");
        setIsInitializing(false);
        return;
      }

      // Fetch ALL questions at once using the new bulk endpoint
      const qResponses = await axios.post(`${API_BASE_URL}/api/questions/list`, qIds);
      const fetched = qResponses.data;
      
      if (!fetched || fetched.length === 0) {
        toast.error("Could not retrieve question details.");
        setIsInitializing(false);
        return;
      }
      
      setQuestions(fetched);

      // Group into sections
      const sectionsMap = {};
      fetched.forEach((q, idx) => {
        const subject = q.subject || 'General';
        if (!sectionsMap[subject]) sectionsMap[subject] = [];
        sectionsMap[subject].push(idx);
      });

      const sectionList = Object.keys(sectionsMap).map(name => ({
        name,
        questionIndices: sectionsMap[name]
      }));
      setSections(sectionList);

      // Initialize statuses
      const initStatuses = {};
      fetched.forEach(q => {
        initStatuses[q.id] = 0; // Not Visited
      });
      setStatuses(initStatuses);

      setIsInitializing(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load test.");
      setIsInitializing(false);
    }
  }, [testId]);

  useEffect(() => {
    fetchTestData();
  }, [fetchTestData]);

  const handleFinalSubmit = useCallback(async () => {
    if (isSubmitted) return;
    setIsSubmitted(true);
    setShowSummary(false);

    let correct = 0;
    let wrong = 0;
    let unattempted = 0;

    const marksForCorrect = 4;
    const marksForWrong = -1;

    // Subject breakdown logic
    const breakdowns = {
      Physics: { score: 0, max: 0 },
      Chemistry: { score: 0, max: 0 },
      Mathematics: { score: 0, max: 0 },
      Biology: { score: 0, max: 0 },
      General: { score: 0, max: 0 }
    };

    questions.forEach(q => {
      const subject = q.subject || 'General';
      const userAns = answers[q.id];
      const status = statuses[q.id];
      
      // Standard subjects mapping (handling potential name variations)
      let key = 'General';
      if (subject.toLowerCase().includes('physic')) key = 'Physics';
      else if (subject.toLowerCase().includes('chem')) key = 'Chemistry';
      else if (subject.toLowerCase().includes('math')) key = 'Mathematics';
      else if (subject.toLowerCase().includes('biol')) key = 'Biology';

      breakdowns[key].max += marksForCorrect;

      // Evaluation logic: 2 (Answered) and 4 (Answered & Marked for Review) are evaluated
      if (status === 2 || status === 4) {
        if (userAns === q.correctOption) {
          correct++;
          breakdowns[key].score += marksForCorrect;
        } else {
          wrong++;
          breakdowns[key].score += marksForWrong;
        }
      } else {
        unattempted++;
      }
    });

    const totalScore = (correct * marksForCorrect) + (wrong * marksForWrong);
    const maxScore = questions.length * marksForCorrect;
    const accuracy = (correct + wrong) > 0 ? Math.round((correct / (correct + wrong)) * 100) : 0;

    setScoreData({ correct, wrong, unattempted, totalScore, maxScore, accuracy });

    // Send to backend
    try {
      if (user.email && testConf) {
        const resultPayload = {
          testName: testConf.title,
          date: new Date().toLocaleDateString('en-CA'),
          marksObtained: totalScore,
          totalMarks: maxScore,
          studentEmail: user.email.toLowerCase().trim(),
          
          // Subject Breakdown
          physicsScore: breakdowns.Physics.score,
          physicsMax: breakdowns.Physics.max,
          chemScore: breakdowns.Chemistry.score,
          chemMax: breakdowns.Chemistry.max,
          mathScore: breakdowns.Mathematics.score,
          mathMax: breakdowns.Mathematics.max,
          bioScore: breakdowns.Biology.score,
          bioMax: breakdowns.Biology.max
        };

        await axios.post(
          `${API_BASE_URL}/api/students/email/${user.email.toLowerCase().trim()}/results`, 
          resultPayload,
          { 
            headers: { 
              'X-Tenant-ID': instituteId,
              'Authorization': `Bearer ${user.token}`
            } 
          }
        );
        toast.success("Assessment results synced successfully!");
      }
    } catch (err) {
      console.error("Result sync failed:", err);
      toast.error("Results calculation saved locally, but failed to sync to server.");
    }
  }, [questions, answers, statuses, testConf, user.email, user.token, isSubmitted, instituteId]);

  // Window Leave detection
  useEffect(() => {
    if (isSubmitted || isInitializing) return;

    let outsideInterval;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setWindowLeaveCount(prev => {
          const next = prev + 1;
          if (next > 3) {
            toast.error("Test auto-submitted: Exceeded maximum window leave limit (3 times).");
            handleFinalSubmit();
          } else {
            toast.warning(`Warning: You left the test window. (${next}/3 times). Test will auto-submit on 4th attempt.`);
          }
          return next;
        });

        outsideInterval = setInterval(() => {
          setTotalOutsideTime(prev => {
            const next = prev + 1;
            if (next >= 30) {
              toast.error("Test auto-submitted: Spent more than 30 seconds outside the test window.");
              handleFinalSubmit();
              clearInterval(outsideInterval);
            }
            return next;
          });
        }, 1000);
      } else {
        if (outsideInterval) clearInterval(outsideInterval);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (outsideInterval) clearInterval(outsideInterval);
    };
  }, [isSubmitted, isInitializing, handleFinalSubmit]);

  // Timer logic
  useEffect(() => {
    if (timeLeft === null || isSubmitted) return;
    if (timeLeft <= 0) {
      handleFinalSubmit();
      return;
    }
    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [timeLeft, isSubmitted, handleFinalSubmit]);

  // Handle auto-status "Not Answered" on visit
  useEffect(() => {
    if (questions.length > 0 && !isSubmitted) {
      const qId = questions[currentIndex].id;
      if (statuses[qId] === 0) {
        setStatuses(prev => ({ ...prev, [qId]: 1 }));
      }
    }
  }, [currentIndex, questions, statuses, isSubmitted]);

  const handleSelectOption = (optText) => {
    if (isSubmitted) return;
    const qId = questions[currentIndex].id;
    setAnswers(prev => ({ ...prev, [qId]: optText }));
  };

  const handleClearResponse = () => {
    const qId = questions[currentIndex].id;
    setAnswers(prev => {
      const newAns = { ...prev };
      delete newAns[qId];
      return newAns;
    });
    // Record it as Not Answered if it was previously answered
    setStatuses(prev => ({ ...prev, [qId]: 1 }));
  };

  const handleSaveAndNext = () => {
    const qId = questions[currentIndex].id;
    if (answers[qId]) {
      setStatuses(prev => ({ ...prev, [qId]: 2 }));
    } else {
      setStatuses(prev => ({ ...prev, [qId]: 1 }));
    }
    moveToNext();
  };

  const handleMarkForReviewAndNext = () => {
    const qId = questions[currentIndex].id;
    const isAnswered = Boolean(answers[qId]);
    setStatuses(prev => ({ ...prev, [qId]: isAnswered ? 4 : 3 }));
    moveToNext();
  };

  const moveToNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      
      // Update section if needed
      const nextIdx = currentIndex + 1;
      const currentSection = sections[activeSectionIdx];
      if (!currentSection.questionIndices.includes(nextIdx)) {
        const newSecIdx = sections.findIndex(sec => sec.questionIndices.includes(nextIdx));
        if (newSecIdx !== -1) setActiveSectionIdx(newSecIdx);
      }
    }
  };

  const handleQuestionPaletteClick = (idx) => {
    setCurrentIndex(idx);
    const newSecIdx = sections.findIndex(sec => sec.questionIndices.includes(idx));
    if (newSecIdx !== -1) setActiveSectionIdx(newSecIdx);
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8">
        <div className="w-16 h-16 border-4 border-t-indigo-500 border-slate-800 rounded-full animate-spin mb-4"></div>
        <div className="text-white animate-pulse text-xl font-medium tracking-widest uppercase">Initializing CBA...</div>
      </div>
    );
  }

  if (isSubmitted && scoreData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl border-t-8 border-indigo-600 overflow-hidden">
          <div className="p-8 text-center border-b border-slate-100">
             <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
             </div>
             <h1 className="text-3xl font-extrabold text-slate-800 mb-1">Assessment Complete</h1>
             <p className="text-slate-500 font-medium">Thank you for appearing for the {testConf.title}.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 p-8 bg-slate-50 text-left">
             <div className="text-center">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Score</div>
                <div className="text-2xl font-black text-indigo-600">{scoreData.totalScore} <span className="text-xs text-slate-400 font-bold">/ {scoreData.maxScore}</span></div>
             </div>
             <div className="text-center">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Accuracy</div>
                <div className="text-2xl font-black text-sky-500">{scoreData.accuracy}%</div>
             </div>
             <div className="text-center">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Correct</div>
                <div className="text-2xl font-black text-emerald-500">{scoreData.correct}</div>
             </div>
              <div className="text-center">
                 <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Incorrect</div>
                 <div className="text-2xl font-black text-rose-500">{scoreData.wrong}</div>
              </div>
              <div className="text-center border-t border-slate-200 pt-4 col-span-1">
                 <div className="text-[10px] font-bold text-rose-400 uppercase tracking-widest mb-1">Window Violations</div>
                 <div className="text-xl font-black text-rose-500">{windowLeaveCount} / 3</div>
              </div>
              <div className="text-center border-t border-slate-200 pt-4 col-span-1">
                 <div className="text-[10px] font-bold text-rose-400 uppercase tracking-widest mb-1">Time Outside</div>
                 <div className="text-xl font-black text-rose-500">{totalOutsideTime}s / 30s</div>
              </div>
           </div>
          <div className="p-6 flex justify-center">
             <button 
               onClick={() => navigate(`/${instituteId}/lms/tests`)} 
               className="px-10 py-3 bg-slate-900 text-white rounded font-bold hover:bg-slate-800 transition-all active:scale-95"
             >
                Return to Dashboard
             </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentIndex];

  return (
    <div className="fixed inset-0 bg-white flex flex-col z-50 overflow-hidden font-sans select-none">
      
      {/* Header Bar */}
      <div className="h-14 bg-indigo-700 text-white flex items-center justify-between px-6 shrink-0 shadow-md">
         <div className="flex items-center gap-4">
            <span className="font-bold text-lg tracking-tight">{testConf.title.toUpperCase()}</span>
         </div>
         <div className="flex items-center gap-1.5 h-full">
            <div className="bg-indigo-800 h-full px-6 flex items-center border-l border-indigo-600/50">
               <span className="text-xs font-bold mr-3 opacity-80">TIME LEFT:</span>
               <span className="text-xl font-black tracking-tighter tabular-nums">{formatTime(timeLeft)}</span>
            </div>
         </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* Left Side: Question Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          
          {/* Section Tabs */}
          <div className="bg-slate-100 border-b border-slate-200 flex shrink-0">
             {sections.map((sec, sIdx) => (
                <button 
                  key={sIdx}
                  onClick={() => {
                    setActiveSectionIdx(sIdx);
                    setCurrentIndex(sec.questionIndices[0]);
                  }}
                  className={`px-8 py-3 text-sm font-bold border-r border-slate-200 transition-all ${
                    activeSectionIdx === sIdx 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {sec.name.toUpperCase()}
                </button>
             ))}
          </div>

          {/* Question Meta */}
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-2 flex items-center justify-between shrink-0">
             <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                Question No. {currentIndex + 1}
             </div>
          </div>

          {/* Question Text Panel */}
          <div className="flex-1 overflow-y-auto p-10 bg-white text-slate-900">
             <div className="max-w-4xl mx-auto">
                <div className="text-xl leading-relaxed mb-12">
                   <Latex>{currentQ.text || ''}</Latex>
                </div>

                <div className="grid grid-cols-1 gap-4">
                   {[currentQ.optionA, currentQ.optionB, currentQ.optionC, currentQ.optionD].map((opt, oIdx) => {
                     const optChar = String.fromCharCode(65 + oIdx);
                     const isSelected = answers[currentQ.id] === opt;
                     return (
                       <label 
                         key={oIdx}
                         className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                           isSelected 
                           ? 'bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200' 
                           : 'bg-white border-slate-200 hover:bg-slate-50'
                         }`}
                       >
                         <input 
                           type="radio" 
                           name={`q-${currentQ.id}`} 
                           checked={isSelected}
                           onChange={() => handleSelectOption(opt)}
                           className="w-5 h-5 accent-indigo-600"
                         />
                         <span className="text-sm font-bold text-slate-400 min-w-[20px]">{optChar})</span>
                         <div className="text-md font-medium">
                            <Latex>{opt || ''}</Latex>
                         </div>
                       </label>
                     );
                   })}
                </div>
             </div>
          </div>

          {/* Bottom Action Bar */}
          <div className="h-16 border-t border-slate-200 flex items-center justify-between px-6 bg-slate-50 shrink-0">
             <div className="flex gap-3">
                <button 
                  onClick={handleMarkForReviewAndNext}
                  className="px-5 py-2.5 rounded border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-sm font-bold"
                >
                   Mark for Review & Next
                </button>
                <button 
                  onClick={handleClearResponse}
                  className="px-5 py-2.5 rounded border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-sm font-bold"
                >
                   Clear Response
                </button>
             </div>
             <div>
                <button 
                  onClick={handleSaveAndNext}
                  className="px-10 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-bold shadow-lg shadow-indigo-600/20"
                >
                   Save & Next
                </button>
             </div>
          </div>
        </div>

        {/* Right Side: Palette/Info */}
        <div className="w-[340px] border-l border-slate-200 flex flex-col shrink-0 bg-slate-50">
          
          {/* User Profile Info */}
          <div className="p-6 border-b border-slate-200 bg-white flex items-center gap-4">
             <div className="w-16 h-20 bg-slate-200 rounded border-2 border-slate-100 flex items-center justify-center shrink-0">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
             </div>
             <div className="overflow-hidden">
                <div className="text-xs text-slate-400 font-bold uppercase mb-1">Candidate</div>
                <div className="text-sm font-black text-slate-800 truncate">{user.name || 'Student Name'}</div>
                <div className="text-[10px] text-indigo-500 font-bold tracking-tighter truncate mt-1">{user.email}</div>
             </div>
          </div>

          {/* Question Status Legend */}
          <div className="p-4 bg-slate-50 border-b border-slate-200">
             <div className="grid grid-cols-2 gap-y-3 gap-x-2">
                <div className="flex items-center gap-2">
                   <div className="w-6 h-6 bg-emerald-500 text-white rounded flex items-center justify-center text-[10px] font-bold">
                      {Object.values(statuses).filter(s => s === 2).length}
                   </div>
                   <span className="text-[10px] font-bold text-slate-500 uppercase">Answered</span>
                </div>
                <div className="flex items-center gap-2">
                   <div className="w-6 h-6 bg-rose-500 text-white rounded flex items-center justify-center text-[10px] font-bold">
                      {Object.values(statuses).filter(s => s === 1).length}
                   </div>
                   <span className="text-[10px] font-bold text-slate-500 uppercase">Not Answered</span>
                </div>
                <div className="flex items-center gap-2">
                   <div className="w-6 h-6 bg-slate-200 text-slate-500 border border-slate-300 rounded flex items-center justify-center text-[10px] font-bold">
                      {Object.values(statuses).filter(s => s === 0).length}
                   </div>
                   <span className="text-[10px] font-bold text-slate-500 uppercase">Not Visited</span>
                </div>
                <div className="flex items-center gap-2">
                   <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                      {Object.values(statuses).filter(s => s === 3).length}
                   </div>
                   <span className="text-[10px] font-bold text-slate-500 uppercase">Marked for Review</span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                   <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold relative">
                      {Object.values(statuses).filter(s => s === 4).length}
                      <div className="absolute top-0 right-0 w-2 h-2 bg-emerald-400 rounded-full border border-purple-600" />
                   </div>
                   <span className="text-[10px] font-bold text-slate-500 uppercase">Answered & Marked Review <br/><i className="lowercase font-normal text-sky-500">(Will be evaluated)</i></span>
                </div>
             </div>
          </div>

          {/* Question Palette */}
          <div className="flex-1 overflow-y-auto p-4 bg-white">
             <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Question Palette</div>
             <div className="grid grid-cols-5 gap-2.5">
                {questions.map((q, idx) => {
                  const st = statuses[q.id];
                  let bgColor = 'bg-slate-200 text-slate-600 hover:bg-slate-300';
                  let shape = 'rounded';
                  
                  if (st === 1) bgColor = 'bg-rose-500 text-white';
                  if (st === 2) bgColor = 'bg-emerald-500 text-white';
                  if (st === 3) { bgColor = 'bg-purple-600 text-white'; shape = 'rounded-full'; }
                  if (st === 4) { bgColor = 'bg-purple-600 text-white'; shape = 'rounded-full'; }

                  return (
                    <button 
                      key={idx}
                      onClick={() => handleQuestionPaletteClick(idx)}
                      className={`w-10 h-10 ${shape} flex items-center justify-center font-bold text-sm transition-all relative ${bgColor} ${currentIndex === idx ? 'ring-2 ring-offset-2 ring-indigo-500 scale-110 z-10' : ''}`}
                    >
                      {idx + 1}
                      {st === 4 && <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-purple-600" />}
                    </button>
                  );
                })}
             </div>
          </div>

          {/* Submit Test Button */}
          <div className="p-4 border-t border-slate-200 bg-slate-50 shrink-0">
             <button 
               onClick={() => setShowSummary(true)}
               className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white text-md font-bold rounded-lg transition-all active:scale-95 shadow-lg shadow-emerald-600/20"
             >
                SUBMIT TEST
             </button>
          </div>
        </div>
      </div>

      {/* Summary Modal Overlay */}
      {showSummary && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-6">
           <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
              <div className="p-6 border-b border-slate-100">
                 <h2 className="text-xl font-black text-slate-800">Exam Summary</h2>
              </div>
              <div className="p-8">
                 <table className="w-full text-sm font-bold text-slate-600">
                    <tbody className="divide-y divide-slate-100">
                       <tr className="py-3 flex justify-between">
                          <td>Total Questions</td>
                          <td className="text-slate-900">{questions.length}</td>
                       </tr>
                       <tr className="py-3 flex justify-between">
                          <td>Answered</td>
                          <td className="text-emerald-600">{Object.values(statuses).filter(s => s === 2).length}</td>
                       </tr>
                       <tr className="py-3 flex justify-between">
                          <td>Not Answered</td>
                          <td className="text-rose-500">{Object.values(statuses).filter(s => s === 1).length}</td>
                       </tr>
                       <tr className="py-3 flex justify-between">
                          <td>Marked for Review</td>
                          <td className="text-purple-600">{Object.values(statuses).filter(s => s === 3).length}</td>
                       </tr>
                       <tr className="py-3 flex justify-between">
                          <td>Answered & Marked Review</td>
                          <td className="text-indigo-600">{Object.values(statuses).filter(s => s === 4).length}</td>
                       </tr>
                       <tr className="py-3 flex justify-between">
                          <td>Not Visited</td>
                          <td className="text-slate-400">{Object.values(statuses).filter(s => s === 0).length}</td>
                       </tr>
                       <tr className="py-3 flex justify-between border-t border-rose-100 mt-2 pt-4">
                          <td className="text-rose-600 font-bold">Window Violations</td>
                          <td className="text-rose-600 font-black">{windowLeaveCount} / 3</td>
                       </tr>
                       <tr className="py-3 flex justify-between">
                          <td className="text-rose-600 font-bold">Time spent outside</td>
                          <td className="text-rose-600 font-black">{totalOutsideTime}s / 30s</td>
                       </tr>
                    </tbody>
                 </table>
                 <p className="mt-8 text-xs text-rose-500 font-bold bg-rose-50 p-3 rounded text-center">Are you sure you want to submit the assessment? No changes will be allowed after final submission.</p>
              </div>
              <div className="p-6 bg-slate-50 flex gap-3">
                 <button onClick={() => setShowSummary(false)} className="flex-1 px-6 py-3 rounded border border-slate-300 bg-white font-bold text-slate-600 hover:bg-slate-100">No, Continue Exam</button>
                 <button onClick={handleFinalSubmit} className="flex-1 px-6 py-3 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg shadow-emerald-600/20">Yes, Final Submit</button>
              </div>
           </div>
        </div>
      )}

    </div>
  );
}
